import { useState, useCallback } from 'react';
import { logger } from '../utils/logger';

interface ConfirmActionState<T> {
  isOpen: boolean;
  isLoading: boolean;
  targetId: T | null;
}

interface UseConfirmActionResult<T> {
  isOpen: boolean;
  isLoading: boolean;
  targetId: T | null;
  open: (id: T) => void;
  cancel: () => void;
  confirm: () => Promise<void>;
}

export function useConfirmAction<T = number>(
  onConfirm: (id: T) => Promise<void>,
  options?: {
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
  }
): UseConfirmActionResult<T> {
  const [state, setState] = useState<ConfirmActionState<T>>({
    isOpen: false,
    isLoading: false,
    targetId: null,
  });

  const open = useCallback((id: T) => {
    setState({ isOpen: true, isLoading: false, targetId: id });
  }, []);

  const cancel = useCallback(() => {
    setState({ isOpen: false, isLoading: false, targetId: null });
  }, []);

  const confirm = useCallback(async () => {
    if (state.targetId === null) return;

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      await onConfirm(state.targetId);
      setState({ isOpen: false, isLoading: false, targetId: null });
      options?.onSuccess?.();
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      if (options?.onError) {
        options.onError(error);
      } else {
        logger.error('Confirm action failed:', error);
      }
    }
  }, [state.targetId, onConfirm, options]);

  return {
    isOpen: state.isOpen,
    isLoading: state.isLoading,
    targetId: state.targetId,
    open,
    cancel,
    confirm,
  };
}

export default useConfirmAction;
